import React, { useCallback, useEffect, useState } from 'react';
import { TypedSupabaseClient } from '../../lib/supabaseClient';
import {
  ContinuingReviewQueueRow,
  ContinuingReviewReminderRow,
  listRemindersForSubmission,
  sendContinuingReviewReminder,
} from '../../services/continuingReviewService';
import {
  MAX_CUSTOM_MESSAGE_LENGTH,
  parseCcEmails,
  isValidEmail,
  relativeTimeFrom,
  reminderStatusBadgeClass,
  reminderTemplateHint,
} from './continuingReviewQueueUtils';

interface ReminderModalProps {
  row: ContinuingReviewQueueRow;
  supabase: TypedSupabaseClient;
  onClose: () => void;
  /** Called after a reminder is successfully sent so the queue can refresh. */
  onSent: () => void;
}

type SendState = 'idle' | 'sending';

const ReminderModal: React.FC<ReminderModalProps> = ({ row, supabase, onClose, onSent }) => {
  const [recipient, setRecipient] = useState(row.user_email ?? '');
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendState, setSendState] = useState<SendState>('idle');

  const [history, setHistory] = useState<ContinuingReviewReminderRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const rows = await listRemindersForSubmission(supabase, row.source_submission_id);
      setHistory(rows);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Unable to load reminder history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [supabase, row.source_submission_id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSend = async () => {
    setValidationError(null);
    setServerError(null);
    setSuccessMessage(null);

    const trimmedRecipient = recipient.trim();
    if (!isValidEmail(trimmedRecipient)) {
      setValidationError('Enter a valid recipient email address.');
      return;
    }

    const { emails: ccEmails, invalid } = parseCcEmails(cc);
    if (invalid.length > 0) {
      setValidationError(`Invalid CC address: ${invalid.join(', ')}`);
      return;
    }

    if (message.length > MAX_CUSTOM_MESSAGE_LENGTH) {
      setValidationError(`Custom message must be ${MAX_CUSTOM_MESSAGE_LENGTH} characters or fewer.`);
      return;
    }

    setSendState('sending');
    try {
      const result = await sendContinuingReviewReminder(supabase, {
        sourceSubmissionId: row.source_submission_id,
        recipientEmail: trimmedRecipient,
        ccEmails: ccEmails.length > 0 ? ccEmails : undefined,
        customMessage: message.trim().length > 0 ? message.trim() : undefined,
      });

      if (result.status === 'duplicate') {
        setSuccessMessage('A reminder for this state was already sent today.');
      } else {
        setSuccessMessage('Reminder sent.');
      }
      onSent();
      await loadHistory();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Unable to send reminder.');
    } finally {
      setSendState('idle');
    }
  };

  const sending = sendState === 'sending';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Send continuing review reminder"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Send reminder</h3>
            <p className="mt-1 text-sm text-gray-500">
              {row.protocol_title || 'Untitled protocol'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">{reminderTemplateHint(row.queue_state)}</p>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="reminder-recipient">
              Recipient
            </label>
            <input
              id="reminder-recipient"
              type="email"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="reminder-cc">
              CC (optional, comma-separated)
            </label>
            <input
              id="reminder-cc"
              type="text"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              value={cc}
              placeholder="coordinator@example.com, pi@example.com"
              onChange={(e) => setCc(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="reminder-message">
              Custom message (optional)
            </label>
            <textarea
              id="reminder-message"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              rows={4}
              maxLength={MAX_CUSTOM_MESSAGE_LENGTH}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              {message.length}/{MAX_CUSTOM_MESSAGE_LENGTH}. Message content is not stored verbatim.
            </p>
          </div>

          {validationError && (
            <p className="text-sm text-red-600" role="alert">
              {validationError}
            </p>
          )}
          {serverError && (
            <p className="text-sm text-red-600" role="alert">
              {serverError}
            </p>
          )}
          {successMessage && (
            <p className="text-sm text-green-700" role="status">
              {successMessage}
            </p>
          )}
        </div>

        <div className="border-t px-6 py-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Reminder history</h4>
          {historyLoading ? (
            <p className="text-sm text-gray-500">Loading history…</p>
          ) : historyError ? (
            <p className="text-sm text-red-600">{historyError}</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500">No reminders sent yet.</p>
          ) : (
            <ul className="space-y-2">
              {history.map((reminder) => (
                <li key={reminder.id} className="text-sm flex flex-col gap-0.5 border rounded-md px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{reminder.recipient_email}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${reminderStatusBadgeClass(reminder.status)}`}
                    >
                      {(reminder.status ?? 'UNKNOWN').toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {relativeTimeFrom(reminder.sent_at ?? reminder.created_at)}
                  </span>
                  {reminder.error_message && (
                    <span className="text-xs text-red-600">{reminder.error_message}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border rounded-md hover:bg-gray-50"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            className="px-4 py-2 text-sm text-white bg-[#F55D3E] rounded-md hover:bg-[#F9A594] disabled:opacity-50"
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send reminder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderModal;
